import React, { useEffect, useMemo } from 'react';
import {
  Button,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableColumn,
  Pill,
  TableActionTypes,
  TableState,
  Typography,
} from 'web-library';
import { useDataTable } from 'web-library/stories/organism/DataTable/DataTable';


import { makeStyles } from 'tss-react/mui';
import { candidateService } from '../../../api';

import { dropDownData } from '../logic/dropDownOption';
import DownloadFromS3Action from '../../../components/buttons/downloadFromS3Action';
import { useAuditDownloadAction } from '../logic/useSelectTransactionLog.';
import { customFilterChipRender } from '../../../hooks/table';
import DownloadAuditProgressModal from '../../../components/modals/DownloadAuditProgressModal';
import { MenuItems } from '../../../components/select/model';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { SelectChangeEvent } from '@mui/material';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(3),
  },
}));

const applicationIdCustomRender = (state: TableState, dataIndex: number) => {
  if (state.data[dataIndex].applicationId === '') {
    return <Typography>N/A</Typography>;
  }
  return <Typography>{state.data[dataIndex].applicationId}</Typography>;
};

const statusCustomRender = (state: TableState, dataIndex: number) => {
  if (!state.data[dataIndex].status) {
    return <Pill variant="info" label="N/A" />;
  }
  if (state.data[dataIndex].status === 'Success') {
    return <Pill variant="positive" label={state.data[dataIndex].status} />;
  }
  if (state.data[dataIndex].status === 'Failure') {
    return <Pill variant="negative" label={state.data[dataIndex].status} />;
  }
  return <Pill variant="info" label={state.data[dataIndex].status} />;
};

const updateCustomFilter = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  action: string,
  pars: [filterList: string[][], filterPos: number, index: number]
): string[][] => {
  if (action === 'accountType') {
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: { ...state.filters, accountType: [] },
    });
  }

  if (action === 'role') {
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: { ...state.filters, role: [] },
    });
  }

  if (action === 'action') {
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: { ...state.filters, action: [] },
    });
  }

  // update table inner filter
  return customFilterChipRender(...pars);
};

const useDataTableColumns = ({
  translations,
}: {
  translations: {
    accountType: string;
    userName: string;
    role: string;
    action: string;
    actionDateTime: string;
    applicationId: string;
    status: string;
  };
}) => {
  const { classes } = useStyles();

  const { data: dataAccount } = candidateService.audit.opadmin.useAuditAccountTypeListQuery();

  const { data: dataRole } = candidateService.audit.opadmin.useAuditRoleListQuery();

  const { state, dispatch, tOptions, selectionToolbar, remoteTotalRowsSelected } = useDataTable();

  const { data: dataAction } = candidateService.audit.opadmin.useAuditActionListQuery();

  /** reform data into CustomSelect MenuItems
   * */
  const actionDropDownData = useMemo<MenuItems[]>(() => {
    if (!dataAction) {
      return [];
    }
    return dataAction.map((option) => ({
      children: option,
      value: option,
      key: `k-${option}`,
    }));
  }, [dataAction]);

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'accountType',
      label: translations.accountType,
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          update: (filterList: string[][],
            filterPos: number,
            index: number) =>
            updateCustomFilter(
              dispatch,
              state,
              'accountType',
              [filterList, filterPos, index]
            ),
        },
        filterOptions: {
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              label={translations.accountType}
              placeholder="Select Account Type"
              displayEmpty
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
              value={filterList[index][0]}
              menuItems={dropDownData(dataAccount)}
              variant="filled"
            />
          ),
          fullWidth: true,
        },
      },
    },
    {
      name: 'username',
      label: translations.userName,
      options: {
        sort: false,
        filter: false,
      },
    },
    {
      name: 'role',
      label: translations.role,
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          // @ts-ignore
          update: (...pars) => updateCustomFilter(dispatch, state, 'role', pars),
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              label={translations.role}
              placeholder="Select Role"
              displayEmpty
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              menuItems={dropDownData(dataRole)}
              // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
              value={filterList[index][0]}
              variant="filled"
            />
          ),
        },
      },
    },
    {
      name: 'action',
      label: translations.action,
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v: any) => {
            if (v.length > 1) {
              return [`${v[0]}/${v[1]}`];
            }
            if (v.length === 1) {
              return [`${v[0]}`];
            }
            return [];
          },
          // @ts-ignore
          update: (...pars) => updateCustomFilter(dispatch, state, 'action', pars),
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              displayEmpty
              defaultValue={undefined}
              inputProps={{ style: { width: 100 } }}
              label={translations.action}
              labelId="action-options"
              id="action-options"
              onChange={(e: SelectChangeEvent<unknown>) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index] = e.target.value as string[];
                onChange(filterList[index], index, column);
                window.dispatchEvent(new CustomEvent('resize'));
              }}
              menuItems={actionDropDownData}
              // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
              value={filterList[index]}
              variant="filled"
              multiple
              renderValue={(selected: unknown | string[]) =>
                Array.isArray(selected) && selected.length > 0
                  ? selected.join(', ')
                  : 'Select Actions'
              }
            />
          ),
        },
      },
    },
    {
      name: 'actionDateTime',
      label: translations.actionDateTime,
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) =>
          stringToLongDateAndShortTime(state.data[dataIndex].actionDateTime),
      },
    },
    {
      name: 'applicationId',
      label: translations.applicationId,
      options: {
        sort: false,
        filter: false,
        // @ts-ignore
        customBodyRenderLite: (dataIndex: number) => applicationIdCustomRender(state, dataIndex),
      },
    },
    {
      name: 'status',
      label: translations.status,
      options: {
        sort: false,
        filter: false,
        // @ts-ignore
        customBodyRenderLite: (dataIndex: number) => statusCustomRender(state, dataIndex),
      },
    },
  ];

  return {
    tableColumns,
    state,
    dispatch,
    tOptions,
    selectionToolbar,
    remoteTotalRowsSelected,
  };
};

const SelectTransactionLog = ({
  translations,
  initData,
  onHandleFetchingTableData,
  isFetchingTableData,
}: {
  translations: {
    accountType: string;
    userName: string;
    role: string;
    action: string;
    actionDateTime: string;
    applicationId: string;
    status: string;
  };
  initData: any;
  onHandleFetchingTableData: () => void;
  isFetchingTableData: boolean;
}) => {
  const { tableColumns, state, dispatch, tOptions, selectionToolbar } = useDataTableColumns({
    translations,
  });

  const { mutate, isLoading } = candidateService.audit.opadmin.useAuditSearchList(
    (res) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.data.content,
          count: res.data.totalElements,
        },
      } as TableActionTypes);
      onHandleFetchingTableData();
    },
    () => null
  );

  const {
    dialogState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogClose,
  } = useAuditDownloadAction(
    initData.startDate,
    initData.endDate,
    state.filters.accountType,
    state.filters.role,
    state.filters.action
  );

  useEffect(() => {
    if (initData.startDate !== '' || initData.endDate !== '') {
      mutate({
        startDate: initData.startDate,
        endDate: initData.endDate,
        size: state.size,
        page: state.page,
        accountType: state.filters.accountType,
        roles: state.filters.role,
        action: state.filters.action,
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.size, state.page, state.filters, initData]);

  /** append server size pagination to default column options
   * */
  const appendedServerSizePaginationColumnOption = useMemo<MUIDataTableOptions>(() => {
    const customizeToolBar = () => {
      if (state.count === 0) {
        return (
          <Button variant="text" color="primary" disabled>
            Export
          </Button>
        );
      }
      return (
        <DownloadFromS3Action
          color="primary"
          actionType="link"
          requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
          label="Export"
          onClick={() => {
            onHandleExport();
          }}
          onDownload={(status) => downloadCSV(status)}
        />
      );
    };

    return {
      filter: true,
      pagination: true,
      customToolbar: customizeToolBar,
      selectToolbarPlacement: 'replace',
    };
  }, [state, downloadCSV, fileUUID, onHandleExport]);

  return (
    <>
      <MUIDataTable
        // @ts-ignore
        state={state}
        dispatch={dispatch}
        columns={tableColumns}
        // @ts-ignore
        data={state.data}
        isLoading={isFetchingTableData || isLoading}
        components={{ TableToolbarSelect: () => selectionToolbar(undefined) }}
        options={{ ...tOptions, ...appendedServerSizePaginationColumnOption }}
      />
      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogState}
        onDialogClose={onDialogClose}
      />
    </>
  );
};

export default SelectTransactionLog;
